import type { DatasetConfig } from '../types';

export const portfolioAssetsConfig: DatasetConfig = {
  id: 'portfolioAssets',
  usesGlobalText: true,
  label: 'Portfolio Assets',
  searchEndpoint: '/queries/portfolio_assets',
  // Joins the Map Query spatial-export roster as the backend 'portfolio_assets' dataset. Attribute ids
  // match GisExport::Sources::MapQuery::PORTFOLIO_ASSET_COLUMNS.
  spatialExport: {
    key: 'portfolio_assets',
    attributes: [
      { id: 'entity_name', label: 'Entity' },
      { id: 'tract_name', label: 'Tract Name' },
      { id: 'interest_type', label: 'Interest Type' },
      { id: 'net_acres', label: 'Net Acres' },
      { id: 'gross_acres', label: 'Gross Acres' },
      { id: 'depth_severance', label: 'Depth Severance' },
    ],
  },
  directHref: '/queries/portfolio_assets',
  geoLocationKey: 'location',
  actionColumnSize: 48,
  // Matches the rendered portfolio-assets fill (#F1C21B) so the color-picker
  // swatch shows the layer's real default instead of black. Lowercase to match
  // the palette entry (the picker's "selected" check is case-sensitive).
  defaultLayerColor: '#f1c21b',
  defaultPayload: {
    text: '',
    page: 1,
    pageSize: 100,
  },

  filters: [
    {
      id: 'entity_name',
      label: 'Entity Name',
      category: 'Entity Name',
      type: 'multi-select',
      param: 'entity_names',
      facetKey: 'entityName',
    },
    {
      id: 'interest_type',
      label: 'Interest Type',
      category: 'Interest Type',
      type: 'multi-select',
      param: 'interest_type',
      facetKey: 'interestType',
    },
    {
      id: 'net_acres',
      label: 'Net Acres',
      category: 'Net Acres',
      type: 'numeric-range',
      minParam: 'min_net_acres',
      maxParam: 'max_net_acres',
    },
    {
      id: 'gross_acres',
      label: 'Gross Acres',
      category: 'Gross Acres',
      type: 'numeric-range',
      minParam: 'min_gross_acres',
      maxParam: 'max_gross_acres',
    },
    {
      id: 'depth_range',
      label: 'Depth Range',
      category: 'Depth Range',
      type: 'numeric-range',
      minParam: 'min_depth',
      maxParam: 'max_depth',
    },
  ],

  columns: [
    {
      id: 'entity_name',
      header: 'Entity',
      accessorKey: 'entityName',
      size: 200,
      sortable: true,
      // Entity names can exceed the column width; wrap to show them in full
      // rather than truncating with an ellipsis (AC #2).
      wrap: true,
    },
    {
      id: 'tract_name',
      header: 'Asset Name',
      accessorKey: 'tractName',
      size: 180,
      sortable: true,
    },
    // The tract's tags as comma text, blank with none (INS-13689). Not sortable: the server's sort
    // allowlist has no tags column. The global search finds a row by a tag.
    {
      id: 'tract_tags',
      header: 'Tags',
      accessorKey: 'tractTags',
      size: 180,
      sortable: false,
      wrap: true,
    },
    {
      id: 'interest_type',
      header: 'Interest Type',
      accessorKey: 'interestType',
      size: 120,
      sortable: true,
    },
    {
      id: 'net_acres',
      header: 'Net Acres',
      accessorKey: 'netAcres',
      size: 110,
      sortable: true,
      type: 'number',
      decimals: 2,
      align: 'left',
    },
    {
      id: 'gross_acres',
      header: 'Gross Acres',
      accessorKey: 'grossAcres',
      size: 140,
      sortable: true,
      type: 'number',
      decimals: 2,
      // Left-aligned to stay consistent with Net Acres.
      align: 'left',
    },
    {
      id: 'depth_severance',
      header: 'Depth Severance',
      accessorKey: 'depthSeverance',
      size: 160,
      sortable: false,
    },
  ],
};
